import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Stethoscope, Loader2, ArrowLeft, ShieldAlert } from "lucide-react";

export default function PasswordResetConfirm() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const token = sp.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Senha deve ter ao menos 6 caracteres");
    if (pw !== pw2) return toast.error("As senhas não coincidem");
    setBusy(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, new_password: pw });
      toast.success("Senha redefinida! Faça login com a nova senha.");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#EBE5DF]">
      <div className="hidden md:flex flex-col justify-between bg-[#0B2230] text-[#EBE5DF] p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20"
             style={{ backgroundImage: "url(https://images.unsplash.com/photo-1648775507324-b48dd3791fa5?w=1200)",
                      backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur-md flex items-center justify-center">
            <Stethoscope className="w-6 h-6" />
          </div>
          <div>
            <div className="font-heading font-semibold text-2xl text-white">Clínica Kloss</div>
            <div className="text-xs uppercase tracking-widest text-white/60">Sistema de Estoque & Protocolos</div>
          </div>
        </div>
        <div className="relative z-10 space-y-3">
          <h1 className="font-heading font-bold text-4xl leading-tight text-white">Escolha uma senha forte.</h1>
          <p className="text-white/70 max-w-md">Use ao menos 6 caracteres. O link é único e será invalidado após esta redefinição.</p>
        </div>
        <div className="relative z-10 text-xs text-white/40 uppercase tracking-widest">Uso restrito · LGPD</div>
      </div>

      <div className="flex items-center justify-center p-8">
        {!token ? (
          <div className="w-full max-w-sm space-y-4" data-testid="reset-no-token">
            <div className="flex items-center gap-3 text-[#991B1B]">
              <ShieldAlert className="w-8 h-8" />
              <div>
                <div className="text-xs uppercase tracking-widest text-[#8C98A0]">Link inválido</div>
                <h2 className="font-heading text-2xl font-semibold text-[#0B2230] mt-1">Token ausente</h2>
              </div>
            </div>
            <p className="text-sm text-[#5C6B73]">Este link não contém um token válido. Solicite uma nova redefinição.</p>
            <Link to="/esqueci-senha" className="inline-flex items-center gap-2 text-sm text-[#0B2230] hover:underline">
              <ArrowLeft className="w-4 h-4" /> Solicitar novo link
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="reset-confirm-form">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#8C98A0]">Redefinir senha</div>
              <h2 className="font-heading text-3xl font-semibold text-[#0B2230] mt-1">Nova senha</h2>
              <p className="text-sm text-[#5C6B73] mt-2">Escolha uma senha para o seu acesso.</p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C6B73] font-medium">Nova senha</label>
              <input
                data-testid="reset-new-password"
                type="password" required minLength={6} autoComplete="new-password"
                value={pw} onChange={(e) => setPw(e.target.value)}
                className="w-full mt-1 px-4 py-3 rounded-lg border border-[#DCD7CE] bg-white focus:outline-none focus:ring-2 focus:ring-[#0B2230]/20"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C6B73] font-medium">Confirmar senha</label>
              <input
                data-testid="reset-confirm-password"
                type="password" required minLength={6} autoComplete="new-password"
                value={pw2} onChange={(e) => setPw2(e.target.value)}
                className="w-full mt-1 px-4 py-3 rounded-lg border border-[#DCD7CE] bg-white focus:outline-none focus:ring-2 focus:ring-[#0B2230]/20"
              />
            </div>
            <button
              data-testid="reset-confirm-submit"
              type="submit" disabled={busy}
              className="w-full bg-[#0B2230] hover:bg-[#143346] disabled:opacity-60 text-white font-medium py-3 rounded-lg transition flex items-center justify-center gap-2"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Redefinir senha
            </button>
            <Link to="/login" className="inline-flex items-center gap-2 text-sm text-[#5C6B73] hover:text-[#0B2230] hover:underline">
              <ArrowLeft className="w-4 h-4" /> Voltar para o login
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
